import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../styles/Hero.css';

const SLIDE_DURATION = 5500;

const Hero = () => {
  const { t } = useTranslation();
  const [current, setCurrent] = useState(0);
  const [animKey, setAnimKey] = useState(0);
  const intervalRef = useRef(null);
  const touchStartX = useRef(null);

  const slides = [
    { image: 'https://images.unsplash.com/photo-1589561454226-796a8aa89b05?w=1200&q=75', label: t('slide1_label'), title: t('slide1_title'), sub: t('slide1_sub') },
    { image: 'https://images.unsplash.com/photo-1541432901042-2d8bd64b4a9b?w=1200&q=75', label: t('slide2_label'), title: t('slide2_title'), sub: t('slide2_sub') },
    { image: 'https://images.unsplash.com/photo-1527838832700-5059252407fa?w=1200&q=75', label: t('slide3_label'), title: t('slide3_title'), sub: t('slide3_sub') },
    { image: 'https://images.unsplash.com/photo-1524231757912-21f4fe3a7200?w=1200&q=75', label: t('slide4_label'), title: t('slide4_title'), sub: t('slide4_sub') },
    { image: 'https://images.unsplash.com/photo-1589561454226-796a8aa89b05?w=1200&q=75&fit=crop&crop=left', label: t('slide5_label'), title: t('slide5_title'), sub: t('slide5_sub') },
  ];

  const goToNext = useCallback(() => {
    setCurrent(c => { const next = (c + 1) % slides.length; setAnimKey(k => k + 1); return next; });
  }, [slides.length]);

  const goToPrev = useCallback(() => {
    setCurrent(c => { const prev = (c - 1 + slides.length) % slides.length; setAnimKey(k => k + 1); return prev; });
  }, [slides.length]);

  const resetTimer = useCallback(() => {
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(goToNext, SLIDE_DURATION);
  }, [goToNext]);

  useEffect(() => {
    intervalRef.current = setInterval(goToNext, SLIDE_DURATION);
    return () => clearInterval(intervalRef.current);
  }, [goToNext]);

  const handleDotClick = (idx) => { setCurrent(idx); setAnimKey(k => k + 1); resetTimer(); };
  const handleArrow = (fn) => { fn(); resetTimer(); };
  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) { if (diff > 0) handleArrow(goToNext); else handleArrow(goToPrev); }
    touchStartX.current = null;
  };

  const slide = slides[current];

  return (
    <section className="hero-slider" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
      {slides.map((s, i) => (
        <div key={i} className={`slide-bg ${i === current ? 'active' : ''}`} style={{ backgroundImage: `url(${s.image})` }} />
      ))}
      <div className="slide-overlay" />
      <div className="slide-content" key={animKey}>
        <span className="slide-label">{slide.label}</span>
        <h1 className="slide-title">{slide.title}</h1>
        <p className="slide-sub">{slide.sub}</p>
        <div className="slide-btns">
          <Link to="/plans" className="slide-btn-primary">{t('cta_primary')}</Link>
          <a href="#process" className="slide-btn-ghost">{t('process_badge')}</a>
        </div>
      </div>
      <button className="slider-arrow slider-prev" onClick={() => handleArrow(goToPrev)} aria-label="Previous slide">&#8249;</button>
      <button className="slider-arrow slider-next" onClick={() => handleArrow(goToNext)} aria-label="Next slide">&#8250;</button>
      <div className="slider-bottom">
        <div className="slider-dots">
          {slides.map((_, i) => (
            <button key={i} className={`slider-dot ${i === current ? 'active' : ''}`} onClick={() => handleDotClick(i)} aria-label={`Slide ${i + 1}`} />
          ))}
        </div>
        <div className="slider-progress"><div className="slider-progress-bar" key={animKey} /></div>
      </div>
    </section>
  );
};

export default Hero;
